import React, { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card'
import { Badge } from './ui/badge'
import { Button } from './ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select'
import { Label } from './ui/label'
import { Input } from './ui/input'
import { Textarea } from './ui/textarea'
import { AlertTriangle, Plus, X, Calendar, User, FileText, Save, AlertCircle, ChevronLeft, ChevronRight, History, Eye } from 'lucide-react'
import { toast } from 'sonner'
import {
  FHIRAllergyIntolerance,
  FHIRAllergyIntoleranceClinicalStatus,
  FHIRAllergyIntoleranceVerificationStatus,
  FHIRAllergyIntoleranceType,
  FHIRAllergyIntoleranceCategory,
  FHIRAllergyIntoleranceCriticality,
  FHIRAllergyIntoleranceSeverity
} from '../types/fhir-allergy-intolerance'
import {
  convertToFHIR,
  convertFromFHIR,
  validateFHIRRecord,
  createDefaultFHIRRecord
} from '../lib/fhir-allergy-mapping'
import { useAllergyReference, useAllergyCodeSearch } from '../hooks/useAllergyReference'
import { EnhancedMedicalRecord } from '../lib/medicalRecordsSystem'
import { AllergyIntoleranceCreateData, AllergyIntoleranceService } from '../lib/allergyIntoleranceService'
import PatientSelector from './PatientSelector'

interface AllergyIntoleranceFormProps {
  patientId?: string
  doctorId: string
  recordId?: string
  mode: 'create' | 'edit'
  initialData?: Partial<EnhancedMedicalRecord>
  onSave?: (record: EnhancedMedicalRecord) => void
  onCancel?: () => void
  allowPatientSelection?: boolean
}

interface ReactionForm {
  manifestation_codes: string[]
  manifestation_displays: string[]
  severity: FHIRAllergyIntoleranceSeverity
  description: string
  onset: string
}

const AllergyIntoleranceForm: React.FC<AllergyIntoleranceFormProps> = ({
  patientId,
  doctorId,
  recordId,
  mode,
  initialData,
  onSave,
  onCancel,
  allowPatientSelection = false
}) => {
  // Estado para seleção de paciente
  const [selectedPatientId, setSelectedPatientId] = useState<string>(patientId || '')
  // Estados do formulário
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [clinicalStatus, setClinicalStatus] = useState<FHIRAllergyIntoleranceClinicalStatus>(FHIRAllergyIntoleranceClinicalStatus.ACTIVE)
  const [verificationStatus, setVerificationStatus] = useState<FHIRAllergyIntoleranceVerificationStatus>(FHIRAllergyIntoleranceVerificationStatus.UNCONFIRMED)
  const [type, setType] = useState<FHIRAllergyIntoleranceType>(FHIRAllergyIntoleranceType.ALLERGY)
  const [categories, setCategories] = useState<FHIRAllergyIntoleranceCategory[]>([FHIRAllergyIntoleranceCategory.FOOD])
  const [criticality, setCriticality] = useState<FHIRAllergyIntoleranceCriticality>(FHIRAllergyIntoleranceCriticality.UNABLE_TO_ASSESS)
  const [substanceCode, setSubstanceCode] = useState('')
  const [substanceDisplay, setSubstanceDisplay] = useState('')
  const [onset, setOnset] = useState('')
  const [recordedDate, setRecordedDate] = useState(new Date().toISOString().split('T')[0])
  const [reactions, setReactions] = useState<ReactionForm[]>([{
    manifestation_codes: [],
    manifestation_displays: [],
    severity: FHIRAllergyIntoleranceSeverity.MILD,
    description: '',
    onset: ''
  }])
  const [notes, setNotes] = useState<string[]>([''])
  const [errors, setErrors] = useState<string[]>([])
  
  const [isCurrentVersion, setIsCurrentVersion] = useState(true)

  // Carregar dados de referência do banco
  const {
    allergyCodes,
    manifestations,
    clinicalStatusCodes,
    verificationStatusCodes,
    isLoading: isLoadingReference,
    error: referenceError
  } = useAllergyReference()

  const { searchAllergyCode, searchManifestation } = useAllergyCodeSearch()
  const [isLoading, setIsLoading] = useState(false)

  // Carregar dados iniciais
  useEffect(() => {
    if (initialData && mode === 'edit') {
      console.log('🔍 [DEBUG] Carregando dados iniciais para edição:', initialData)
      
      // Tentar extrair dados estruturados do campo description se for JSON
      let structuredData: any = {}
      if (initialData.description) {
        try {
          const parsedDescription = JSON.parse(initialData.description)
          if (typeof parsedDescription === 'object' && parsedDescription !== null) {
            structuredData = parsedDescription
            console.log('📋 [DEBUG] Dados extraídos do campo description:', structuredData)
          }
        } catch (e) {
          console.log('📝 [DEBUG] Description não é JSON, usando como texto simples')
        }
      }
      
      // Combinar dados de custom_fields e structuredData (description parseado)
      const customFields: any = { ...structuredData, ...(initialData.custom_fields || {}) }
      console.log('🔧 [DEBUG] Dados combinados para carregamento:', customFields)
      
      // Carregar dados do paciente
      if (initialData.patient_id) {
        setSelectedPatientId(initialData.patient_id)
      }
      
      setTitle(initialData.title || '')
      setDescription(typeof structuredData === 'object' && structuredData.description ? structuredData.description : (initialData.description || ''))
      setClinicalStatus(customFields.clinical_status || FHIRAllergyIntoleranceClinicalStatus.ACTIVE)
      setVerificationStatus(customFields.verification_status || FHIRAllergyIntoleranceVerificationStatus.UNCONFIRMED)
      setType(customFields.allergy_type || customFields.type || FHIRAllergyIntoleranceType.ALLERGY)
      setCategories(customFields.allergy_categories || customFields.category || [FHIRAllergyIntoleranceCategory.FOOD])
      setCriticality(customFields.criticality || FHIRAllergyIntoleranceCriticality.UNABLE_TO_ASSESS)
      setSubstanceCode(customFields.substance_code || '')
      setSubstanceDisplay(customFields.substance_display || '')
      setOnset(customFields.onset || '')
      setRecordedDate(customFields.recorded_date?.split('T')[0] || new Date().toISOString().split('T')[0])
      setReactions(customFields.reactions || [{
        manifestation_codes: [],
        manifestation_displays: [],
        severity: FHIRAllergyIntoleranceSeverity.MILD,
        description: '',
        onset: ''
      }])
      setNotes(customFields.notes || [''])
      
      // Verificar se é versão atual
      setIsCurrentVersion(initialData.is_current_version !== false)
      
      console.log('✅ [DEBUG] Dados carregados com sucesso:', {
        title: initialData.title,
        selectedPatientId: initialData.patient_id,
        substanceDisplay: customFields.substance_display,
        clinicalStatus: customFields.clinical_status,
        reactions: customFields.reactions,
        notes: customFields.notes
      })
    }
  }, [initialData, mode])



  // Adicionar nova reação
  const addReaction = () => {
    setReactions([...reactions, {
      manifestation_codes: [],
      manifestation_displays: [],
      severity: FHIRAllergyIntoleranceSeverity.MILD,
      description: '',
      onset: ''
    }])
  }

  // Remover reação
  const removeReaction = (index: number) => {
    if (reactions.length > 1) {
      setReactions(reactions.filter((_, i) => i !== index))
    }
  }

  // Atualizar reação
  const updateReaction = (index: number, field: keyof ReactionForm, value: any) => {
    const updatedReactions = [...reactions]
    updatedReactions[index] = { ...updatedReactions[index], [field]: value }
    setReactions(updatedReactions)
  }

  // Adicionar manifestação à reação
  const addManifestation = (reactionIndex: number, code: string, display: string) => {
    const updatedReactions = [...reactions]
    const reaction = updatedReactions[reactionIndex]
    
    if (!reaction.manifestation_codes.includes(code)) {
      reaction.manifestation_codes.push(code)
      reaction.manifestation_displays.push(display)
      setReactions(updatedReactions)
    }
  }

  // Remover manifestação da reação
  const removeManifestation = (reactionIndex: number, manifestationIndex: number) => {
    const updatedReactions = [...reactions]
    const reaction = updatedReactions[reactionIndex]
    
    reaction.manifestation_codes.splice(manifestationIndex, 1)
    reaction.manifestation_displays.splice(manifestationIndex, 1)
    setReactions(updatedReactions)
  }

  // Adicionar nota
  const addNote = () => {
    setNotes([...notes, ''])
  }

  // Remover nota
  const removeNote = (index: number) => {
    if (notes.length > 1) {
      setNotes(notes.filter((_, i) => i !== index))
    }
  }

  // Atualizar nota
  const updateNote = (index: number, value: string) => {
    const updatedNotes = [...notes]
    updatedNotes[index] = value
    setNotes(updatedNotes)
  }



  // Buscar substância comum
  const searchSubstance = async (searchTerm: string) => {
    if (!searchTerm) return
    
    const found = await searchAllergyCode(searchTerm)
    if (found) {
      setSubstanceCode(found.code)
      setSubstanceDisplay(found.display)
      if (!title) setTitle(`Alergia a ${found.display}`)
    }
  }

  // Validar formulário
  const validateForm = (): boolean => {
    const newErrors: string[] = []

    if (!selectedPatientId) newErrors.push('Selecione um paciente para vincular o registro médico')
    if (!title.trim()) newErrors.push('Título é obrigatório')
    if (!substanceDisplay.trim()) newErrors.push('Substância é obrigatória')
    if (reactions.some(r => r.manifestation_displays.length === 0)) {
      newErrors.push('Cada reação deve ter pelo menos uma manifestação')
    }

    setErrors(newErrors)
    return newErrors.length === 0
  }

  // Salvar registro
  const handleSave = async () => {
    if (!validateForm()) {
      toast.error('Por favor, corrija os erros no formulário')
      return
    }

    setIsLoading(true)

    try {
      console.log('🔄 Iniciando salvamento de alergia/intolerância...')
      console.log('📋 Dados do formulário:', {
        selectedPatientId,
        doctorId,
        title,
        description,
        clinicalStatus,
        verificationStatus,
        type,
        categories,
        criticality,
        substanceCode,
        substanceDisplay,
        onset,
        recordedDate,
        reactions: reactions.filter(r => r.manifestation_displays.length > 0),
        notes: notes.filter(n => n.trim())
      })

      // Preparar dados para AllergyIntoleranceService
      const allergyData: AllergyIntoleranceCreateData = {
        patient_id: selectedPatientId,
        doctor_id: doctorId,
        title,
        description,
        // Campos obrigatórios como propriedades diretas
        clinical_status: clinicalStatus,
        verification_status: verificationStatus,
        substance_display: substanceDisplay,
        // Campos opcionais
        allergy_type: type,
        allergy_categories: categories,
        criticality,
        substance_code: substanceCode,
        onset,
        recorded_date: recordedDate,
        reactions: reactions.filter(r => r.manifestation_displays.length > 0),
        notes: notes.filter(n => n.trim())
      }

      console.log('💾 Dados preparados para salvamento:', allergyData)

      // Salvar usando AllergyIntoleranceService
      const { data: savedRecord, error: saveError } = await AllergyIntoleranceService.create(allergyData)

      if (saveError) {
        console.error('❌ Erro ao salvar no banco:', saveError)
        setErrors([saveError.message || 'Erro desconhecido ao salvar'])
        toast.error(`Erro ao salvar: ${saveError.message || 'Erro desconhecido'}`)
        return
      }

      if (!savedRecord) {
        console.error('❌ Nenhum registro retornado após salvamento')
        toast.error('Erro: Nenhum registro foi criado')
        return
      }

      console.log('✅ Registro salvo com sucesso:', savedRecord)

      // Chamar callback de salvamento para compatibilidade
      if (onSave) {
        console.log('📞 Chamando callback onSave...')
        onSave(savedRecord)
      }

      toast.success(`Alergia/Intolerância ${mode === 'create' ? 'criada' : 'atualizada'} com sucesso`)
      
      // Limpar erros
      setErrors([])
      
    } catch (error) {
      console.error('💥 Erro crítico ao salvar:', error)
      const errorMessage = error instanceof Error ? error.message : 'Erro interno do sistema'
      setErrors([errorMessage])
      toast.error(`Erro crítico: ${errorMessage}`)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto p-6">
      {/* Cabeçalho */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-yellow-500" />
            {mode === 'create' ? 'Nova Alergia/Intolerância' : 'Editar Alergia/Intolerância'}
          </CardTitle>
          <CardDescription>
            Registro conforme padrão HL7 FHIR R4 AllergyIntolerance
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Erro ao carregar dados de referência */}
      {referenceError && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-5 w-5" />
              <span className="font-medium">Erro ao carregar dados:</span>
            </div>
            <p className="mt-2 text-sm text-red-600">
              Não foi possível carregar os dados de referência. Alguns campos podem não funcionar corretamente.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Erros de validação */}
      {errors.length > 0 && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-red-800 font-medium">
              <AlertCircle className="h-5 w-5" />
              Erros de Validação
            </div>
            <ul className="mt-2 text-sm text-red-600 list-disc list-inside">
              {errors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}



      {/* Seleção de Paciente */}
      {(allowPatientSelection || !patientId) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5 text-blue-500" />
              Seleção de Paciente
            </CardTitle>
            <CardDescription>
              Selecione o paciente para vincular este registro médico
            </CardDescription>
          </CardHeader>
          <CardContent>
            <PatientSelector
              selectedPatientId={selectedPatientId}
              onPatientSelect={(patient) => setSelectedPatientId(patient?.id || '')}
              required={true}
            />
          </CardContent>
        </Card>
      )}

      {/* Informações Básicas */}
      <Card>
        <CardHeader>
          <CardTitle>Informações Básicas</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="title" className="block text-sm font-medium mb-1">Título *</label>
              <input
                id="title"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Ex: Alergia a amendoim"
                disabled={!isCurrentVersion}
                className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${!isCurrentVersion ? 'bg-gray-100 cursor-not-allowed' : ''}`}
              />
            </div>
            <div>
              <label htmlFor="substance" className="block text-sm font-medium mb-1">Substância *</label>
              <div className="flex gap-2">
                <input
                  id="substance"
                  type="text"
                  value={substanceDisplay}
                  onChange={(e) => setSubstanceDisplay(e.target.value)}
                  placeholder="Ex: Amendoim"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <select 
                  onChange={(e) => searchSubstance(e.target.value)}
                  className="w-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={isLoadingReference}
                >
                  <option value="">{isLoadingReference ? 'Carregando...' : 'Comum'}</option>
                  {allergyCodes.map((allergyCode) => (
                    <option key={allergyCode.id} value={allergyCode.display}>
                      {allergyCode.display}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-medium mb-1">Descrição</label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Descrição detalhada da alergia/intolerância"
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </CardContent>
      </Card>

      {/* Status e Classificação */}
      <Card>
        <CardHeader>
          <CardTitle>Status e Classificação</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <Label>Status Clínico *</Label>
              <Select 
                value={clinicalStatus} 
                onValueChange={(value) => setClinicalStatus(value as FHIRAllergyIntoleranceClinicalStatus)}
                disabled={isLoadingReference}
              >
                <SelectTrigger>
                  <SelectValue placeholder={isLoadingReference ? 'Carregando...' : 'Selecione o status'} />
                </SelectTrigger>
                <SelectContent>
                  {clinicalStatusCodes.map((status) => (
                    <SelectItem key={status.id} value={status.code}>
                      {status.display}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Status de Verificação *</Label>
              <Select 
                value={verificationStatus} 
                onValueChange={(value) => setVerificationStatus(value as FHIRAllergyIntoleranceVerificationStatus)}
                disabled={isLoadingReference}
              >
                <SelectTrigger>
                  <SelectValue placeholder={isLoadingReference ? 'Carregando...' : 'Selecione o status'} />
                </SelectTrigger>
                <SelectContent>
                  {verificationStatusCodes.map((status) => (
                    <SelectItem key={status.id} value={status.code}>
                      {status.display}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Tipo</Label>
              <Select value={type} onValueChange={(value) => setType(value as FHIRAllergyIntoleranceType)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={FHIRAllergyIntoleranceType.ALLERGY}>Alergia</SelectItem>
                  <SelectItem value={FHIRAllergyIntoleranceType.INTOLERANCE}>Intolerância</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Criticidade</Label>
              <Select value={criticality} onValueChange={(value) => setCriticality(value as FHIRAllergyIntoleranceCriticality)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={FHIRAllergyIntoleranceCriticality.LOW}>Baixa</SelectItem>
                  <SelectItem value={FHIRAllergyIntoleranceCriticality.HIGH}>Alta</SelectItem>
                  <SelectItem value={FHIRAllergyIntoleranceCriticality.UNABLE_TO_ASSESS}>Não avaliável</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="onset">Data de Início</Label>
              <Input
                id="onset"
                type="date"
                value={onset}
                onChange={(e) => setOnset(e.target.value)}
              />
            </div>

            <div>
              <Label htmlFor="recorded">Data de Registro</Label>
              <Input
                id="recorded"
                type="date"
                value={recordedDate}
                onChange={(e) => setRecordedDate(e.target.value)}
              />
            </div>
          </div>

          {/* Categorias */}
          <div>
            <Label>Categorias</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {Object.values(FHIRAllergyIntoleranceCategory).map((cat) => (
                <Badge
                  key={cat}
                  variant={categories.includes(cat) ? "default" : "outline"}
                  className="cursor-pointer"
                  onClick={() => {
                    if (categories.includes(cat)) {
                      setCategories(categories.filter(c => c !== cat))
                    } else {
                      setCategories([...categories, cat])
                    }
                  }}
                >
                  {cat === 'food' ? 'Alimento' :
                   cat === 'medication' ? 'Medicamento' :
                   cat === 'environment' ? 'Ambiental' : 'Biológico'}
                </Badge>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Reações */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            Reações
            <Button onClick={addReaction} size="sm" variant="outline">
              <Plus className="h-4 w-4 mr-2" />
              Adicionar Reação
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {reactions.map((reaction, index) => (
            <Card key={index} className="border-gray-200">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm">Reação {index + 1}</CardTitle>
                  {reactions.length > 1 && (
                    <Button
                      onClick={() => removeReaction(index)}
                      size="sm"
                      variant="ghost"
                      className="text-red-500 hover:text-red-700"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Severidade</Label>
                    <Select
                      value={reaction.severity}
                      onValueChange={(value) => updateReaction(index, 'severity', value as FHIRAllergyIntoleranceSeverity)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={FHIRAllergyIntoleranceSeverity.MILD}>Leve</SelectItem>
                        <SelectItem value={FHIRAllergyIntoleranceSeverity.MODERATE}>Moderada</SelectItem>
                        <SelectItem value={FHIRAllergyIntoleranceSeverity.SEVERE}>Severa</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Data da Reação</Label>
                    <Input
                      type="date"
                      value={reaction.onset}
                      onChange={(e) => updateReaction(index, 'onset', e.target.value)}
                    />
                  </div>
                </div>

                <div>
                  <Label>Descrição</Label>
                  <Textarea
                    value={reaction.description}
                    onChange={(e) => updateReaction(index, 'description', e.target.value)}
                    placeholder="Descrição da reação"
                    rows={2}
                  />
                </div>

                {/* Manifestações */}
                <div>
                  <Label>Manifestações</Label>
                  <div className="flex flex-wrap gap-2 mt-2 mb-2">
                    {reaction.manifestation_displays.map((display, manifestationIndex) => (
                      <Badge key={manifestationIndex} variant="secondary" className="flex items-center gap-1">
                        {display}
                        <X
                          className="h-3 w-3 cursor-pointer"
                          onClick={() => removeManifestation(index, manifestationIndex)}
                        />
                      </Badge>
                    ))}
                  </div>
                  <Select 
                    onValueChange={async (value) => {
                      const found = await searchManifestation(value)
                      if (found) {
                        addManifestation(index, found.code, found.display)
                      }
                    }}
                    disabled={isLoadingReference}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={isLoadingReference ? 'Carregando...' : 'Adicionar manifestação'} />
                    </SelectTrigger>
                    <SelectContent>
                      {manifestations.map((manifestation) => (
                        <SelectItem key={manifestation.id} value={manifestation.display}>
                          {manifestation.display}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
          ))}
        </CardContent>
      </Card>

      {/* Notas */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            Notas Adicionais
            <Button onClick={addNote} size="sm" variant="outline">
              <Plus className="h-4 w-4 mr-2" />
              Adicionar Nota
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {notes.map((note, index) => (
            <div key={index} className="flex gap-2">
              <Textarea
                value={note}
                onChange={(e) => updateNote(index, e.target.value)}
                placeholder="Nota adicional"
                rows={2}
                className="flex-1"
              />
              {notes.length > 1 && (
                <Button
                  onClick={() => removeNote(index)}
                  size="sm"
                  variant="ghost"
                  className="text-red-500 hover:text-red-700"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Ações */}
      <div className="flex justify-end gap-3">
        <Button onClick={onCancel} variant="outline">
          Cancelar
        </Button>
        <Button onClick={handleSave} disabled={isLoading}>
          <Save className="h-4 w-4 mr-2" />
          {isLoading ? 'Salvando...' : mode === 'create' ? 'Criar Registro' : 'Salvar Alterações'}
        </Button>
      </div>
    </div>
  )
}

export default AllergyIntoleranceForm